import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, AlertTriangle, BarChart3, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TurnawayRecord {
  id: number;
  agencyId: number;
  date: string;
  reason: string | null;
  numberOfAdults: number;
  numberOfChildren: number;
  referralGiven: boolean;
  referralAgency: string | null;
  notes: string | null;
  createdBy: number | null;
}

interface TurnawayListResponse {
  data: TurnawayRecord[];
  meta: { total: number };
}

interface TurnawayStatsResponse {
  data: {
    total: number;
    totalAdults: number;
    totalChildren: number;
    totalPersons: number;
    byReason: { reason: string; count: number }[];
    byMonth: {
      month: string;
      count: number;
      adults: number;
      children: number;
    }[];
  };
}

const TURNAWAY_REASONS = [
  "No beds available",
  "No appropriate bed (gender/family)",
  "Active substance use",
  "Safety concern",
  "Outside service area",
  "Does not meet eligibility criteria",
  "Other",
];

// ---------------------------------------------------------------------------
// Log Form
// ---------------------------------------------------------------------------

function TurnawayForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().split("T")[0];

  const [date, setDate] = useState(today);
  const [reason, setReason] = useState("");
  const [numberOfAdults, setNumberOfAdults] = useState("1");
  const [numberOfChildren, setNumberOfChildren] = useState("0");
  const [referralGiven, setReferralGiven] = useState(false);
  const [referralAgency, setReferralAgency] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const recordMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/shelter/turnaways", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["turnaways"] });
      queryClient.invalidateQueries({ queryKey: ["turnawayStats"] });
      onClose();
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!date) newErrors.date = "Date is required";
    if (!reason) newErrors.reason = "Reason is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    recordMutation.mutate({
      date,
      reason,
      numberOfAdults: parseInt(numberOfAdults, 10) || 0,
      numberOfChildren: parseInt(numberOfChildren, 10) || 0,
      referralGiven,
      referralAgency: referralAgency || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Log Turnaway</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Date"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              error={errors.date}
            />

            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none text-foreground">
                Reason <span className="text-destructive">*</span>
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={cn(
                  "flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  errors.reason ? "border-destructive" : "border-input",
                )}
              >
                <option value="">-- Select reason --</option>
                {TURNAWAY_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              {errors.reason && (
                <p className="text-xs text-destructive">{errors.reason}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Number of Adults"
              type="number"
              min={0}
              value={numberOfAdults}
              onChange={(e) => setNumberOfAdults(e.target.value)}
            />
            <Input
              label="Number of Children"
              type="number"
              min={0}
              value={numberOfChildren}
              onChange={(e) => setNumberOfChildren(e.target.value)}
            />
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={referralGiven}
                onChange={(e) => setReferralGiven(e.target.checked)}
                className="rounded border-input"
              />
              <span className="text-sm font-medium text-foreground">
                Referral given to another shelter
              </span>
            </label>

            {referralGiven && (
              <Input
                label="Referral Agency"
                value={referralAgency}
                onChange={(e) => setReferralAgency(e.target.value)}
                placeholder="Name of referral agency"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Additional notes..."
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={recordMutation.isPending}>
              <Plus className="h-4 w-4" />
              Log Turnaway
            </Button>
          </div>

          {recordMutation.isError && (
            <p className="text-sm text-destructive">
              Failed to log turnaway. Please try again.
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TurnawayLog Component
// ---------------------------------------------------------------------------

export function TurnawayLog() {
  const today = new Date();
  const [showLogForm, setShowLogForm] = useState(false);

  // Default date range: current quarter
  const quarterStart = useMemo(() => {
    const quarter = Math.floor(today.getMonth() / 3);
    return new Date(today.getFullYear(), quarter * 3, 1)
      .toISOString()
      .split("T")[0] as string;
  }, []);

  const quarterEnd = useMemo(() => {
    const quarter = Math.floor(today.getMonth() / 3);
    const endMonth = quarter * 3 + 3;
    return new Date(today.getFullYear(), endMonth, 0)
      .toISOString()
      .split("T")[0] as string;
  }, []);

  const [startDate, setStartDate] = useState(quarterStart);
  const [endDate, setEndDate] = useState(quarterEnd);

  // List query
  const { data: listData, isLoading: listLoading } =
    useQuery<TurnawayListResponse>({
      queryKey: ["turnaways", startDate, endDate],
      queryFn: () =>
        api.get<TurnawayListResponse>("/shelter/turnaways", {
          params: { startDate, endDate },
        }),
    });

  // Stats query
  const { data: statsData, isLoading: statsLoading } =
    useQuery<TurnawayStatsResponse>({
      queryKey: ["turnawayStats", startDate, endDate],
      queryFn: () =>
        api.get<TurnawayStatsResponse>("/shelter/turnaways/stats", {
          params: { startDate, endDate },
        }),
      enabled: !!startDate && !!endDate,
    });

  const entries = listData?.data || [];
  const stats = statsData?.data;
  const isLoading = listLoading || statsLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Turnaway Log</h1>
          <p className="text-sm text-muted-foreground">
            Track shelter turnaways for VOCA/VAWA reporting
          </p>
        </div>
        <Button onClick={() => setShowLogForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Log Turnaway
        </Button>
      </div>

      {/* Log Form */}
      {showLogForm && <TurnawayForm onClose={() => setShowLogForm(false)} />}

      {/* Summary Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Total Turnaways
                </p>
              </div>
              <p className="mt-2 text-3xl font-bold text-foreground">
                {stats.total}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Adults
                </p>
              </div>
              <p className="mt-2 text-3xl font-bold text-foreground">
                {stats.totalAdults}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-600" />
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Children
                </p>
              </div>
              <p className="mt-2 text-3xl font-bold text-foreground">
                {stats.totalChildren}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-green-600" />
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Total Persons
                </p>
              </div>
              <p className="mt-2 text-3xl font-bold text-foreground">
                {stats.totalPersons}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reason breakdown */}
      {stats && stats.byReason.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By Reason</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.byReason.map((item) => {
                const percentage =
                  stats.total > 0
                    ? Math.round((item.count / stats.total) * 100)
                    : 0;
                return (
                  <div key={item.reason} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{item.reason}</span>
                      <span className="font-medium text-foreground">
                        {item.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-destructive/70"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Date range filter */}
      <div className="flex flex-wrap items-end gap-4">
        <Input
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="w-auto"
        />
        <Input
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-auto"
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && entries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertTriangle className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-medium text-foreground">
              No turnaways recorded
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              No turnaway records found for the selected date range.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {!isLoading && entries.length > 0 && (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Reason
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Adults
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Children
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Referral
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    {entry.date}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {entry.reason || "--"}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-foreground">
                    {entry.numberOfAdults}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-foreground">
                    {entry.numberOfChildren}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {entry.referralGiven ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-950 dark:text-green-300">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        No
                      </span>
                    )}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-sm text-muted-foreground">
                    {entry.notes || "--"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
